/*eslint-disable*/
import React, { memo, useState, forwardRef, useRef, useEffect } from 'react';
import MaskedTextInput from 'react-text-mask';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import * as Styled from './styles';

const Picker = ({
  label,
  selected,
  required = false,
  onChange: handleDateChange,
  dateFormatCalendar,
  maxDate,
  minDate,
  showMonthYearDropdown,
  error,
  isFullWidth,
  isMargin = false,
  idInput = '',
  inputFor = '',
  isActive = false,
  pageColumns,
  ...props
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Handle outside clicks manually with a more robust approach
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!isOpen) return; // Only check if datepicker is open

      const target = event.target;

      // Check if click is outside our component AND outside the datepicker popup
      const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(target);
      const isOutsidePopup =
        !target.closest('.react-datepicker') &&
        !target.closest('.react-datepicker-popper') &&
        !target.closest('.react-datepicker__portal');

      if (isOutsideWrapper && isOutsidePopup) {
        setIsOpen(false);
        if (datePickerRef.current) {
          datePickerRef.current.setOpen(false);
        }
      }
    };

    // Use capture phase to catch the event before it gets stopped
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [isOpen]);

  const handleKeyPress = (e, onClick) => {
    e.keyCode === 13 && onClick();
  };

  const handleCalendarClose = () => {
    setIsFocus(true);
    setIsOpen(false);
  };

  const handleCalendarOpen = () => {
    setIsOpen(true);
  };

  // Function to open calendar when button is clicked
  const handleCalendarButtonClick = () => {
    const newOpenState = !isOpen;

    if (datePickerRef.current) {
      datePickerRef.current.setOpen(newOpenState);
    }
    setIsOpen(newOpenState);
  };

  // Function to validate if the typed date is complete and valid
  const isValidCompleteDate = (dateString) => {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateString)) {
      return false;
    }

    const [month, day, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  const ExampleCustomInput = forwardRef(({ value, onClick, onChange }, ref) => {
    const handleInputChange = (e) => {
      onChange(e);

      const inputValue = e.target.value;
      if (inputValue && inputValue.length === 10 && isValidCompleteDate(inputValue)) {
        const [month, day, year] = inputValue.split('/').map(Number);
        const newDate = new Date(year, month - 1, day);

        const isWithinBounds = (!minDate || newDate >= minDate) && (!maxDate || newDate <= maxDate);

        if (isWithinBounds) {
          handleDateChange(newDate);
          setTimeout(() => {
            if (datePickerRef.current) {
              datePickerRef.current.setOpen(false);
            }
            setIsOpen(false);
          }, 100);
        }
      }
    };

    // Remove the click handler from input - we don't want input clicks to open calendar
    const handleInputClick = (e) => {
      // Just focus the input, don't open calendar
      e.target.focus();
    };

    return (
      <Styled.CustomInputWrapper>
        <MaskedTextInput
          type="text"
          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
          value={value}
          onChange={handleInputChange}
          onClick={handleInputClick} // Changed: no longer opens calendar
          tabIndex={0}
          onKeyDown={(e) => handleKeyPress(e, onClick)}
          disabled={isActive}
          readOnly={isActive}
          id={idInput}
          autoFocus={isFocus}
          aria-labelledby={inputFor}
          onBlur={() => setIsFocus(false)}
          ref={ref}
        />
        <Styled.CalendarButton
          type="button"
          onClick={handleCalendarButtonClick}
          disabled={isActive}
          aria-label="Open calendar"
        />
      </Styled.CustomInputWrapper>
    );
  });

  return (
    <Styled.InputWrap
      ref={wrapperRef}
      className="custom-react-date-picker"
      isMargin={isMargin}
      isFullWidth={isFullWidth}
      pageColumns={pageColumns}
    >
      <Styled.CalendarLabel htmlFor={idInput} id={inputFor} aria-label={label}>
        {label}
      </Styled.CalendarLabel>
      <Styled.ReactPickerWrapper isError={required && error}>
        <DatePicker
          ref={datePickerRef}
          required={required}
          selected={selected}
          onChange={handleDateChange}
          dateFormatCalendar={dateFormatCalendar}
          maxDate={maxDate}
          minDate={minDate}
          onCalendarClose={handleCalendarClose}
          onCalendarOpen={handleCalendarOpen}
          showMonthYearDropdown={showMonthYearDropdown}
          customInput={<ExampleCustomInput />}
          {...props}
        />
        {error && <Styled.Error>{error}</Styled.Error>}
      </Styled.ReactPickerWrapper>
    </Styled.InputWrap>
  );
};

Picker.propTypes = {
  idInput: PropTypes.string.isRequired,
  inputFor: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  label: PropTypes.string,
  required: PropTypes.bool,
  dateFormatCalendar: PropTypes.string,
  showMonthYearDropdown: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isFullWidth: PropTypes.bool,
  isMargin: PropTypes.bool,
  isActive: PropTypes.bool,
  pageColumns: PropTypes.string,
};

Picker.defaultProps = {
  minDate: new Date('01-01-1980'.replace(/-/g, '/')),
  maxDate: new Date(),
  label: '',
  required: false,
  dateFormatCalendar: 'MMMM yyyy',
  showMonthYearDropdown: false,
  error: false,
  isFullWidth: false,
  isMargin: false,
  isActive: false,
  selected: null,
  pageColumns: '',
};

export default memo(Picker);
